// ─────────────────────────────────────────────
//  SharePoint API helpers
//  All network calls live here — components stay clean
// ─────────────────────────────────────────────

import { getAccessToken, getIdToken } from "../../config/msalConfig";
import type {
  CreateUploadJobRequest,
  CreateUploadJobResponse,
  DriveItem,
  FolderItem,
  GraphFolderRaw,
  SiteItem,
  UploadJobItemsResponse,
  UploadJobsSnapshot,
  UploadJobsResponse,
  UploadJobSummary,
} from "../types/sharepoint";

export const BACKEND_URL = process.env.BACKEND_API_URL;

// ── Auth ────────────────────────────────────────────────────────────────────

export async function getToken(): Promise<string> {
  const result = await getAccessToken();
  if (!result.success) throw new Error("Authentication failed — please sign in again.");
  console.log(result)
  return result.accessToken;
}

export async function getBackendToken(): Promise<string> {
  const result = await getIdToken();
  if (!result.success) throw new Error("Backend authentication failed - please sign in again.");
  return result.idToken;
}

// ── Mapping ─────────────────────────────────────────────────────────────────

export function graphFolderToItem(raw: GraphFolderRaw): FolderItem {
  return {
    id: raw.id,
    name: raw.name,
    // If childCount is 0 we already know it's a leaf → set [] to skip lazy load
    children: raw.folder.childCount === 0 ? [] : undefined,
  };
}

// ── Fetchers ─────────────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, token: string): Promise<T> {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Request failed (${res.status}): ${path}`);
  return res.json() as Promise<T>;
}

async function backendApiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getBackendToken();
  const res = await fetch(`${BACKEND_URL}${path}`, {
    ...init,
    headers: {
      ...(init?.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Request failed (${res.status}): ${path}${text ? ` - ${text}` : ""}`);
  }

  return res.json() as Promise<T>;
}

export async function fetchSites(token: string): Promise<SiteItem[]> {
  return apiFetch<SiteItem[]>("/api/sharepoint/sites", token);
}

export async function fetchDrives(siteId: string, token: string): Promise<DriveItem[]> {
  return apiFetch<DriveItem[]>(
    `/api/sharepoint/sites/${encodeURIComponent(siteId)}/drives`,
    token
  );
}

export async function fetchRootFolders(driveId: string, token: string): Promise<FolderItem[]> {
  const raw = await apiFetch<GraphFolderRaw[]>(
    `/api/sharepoint/drives/${driveId}/folders`,
    token
  );
  return raw.map(graphFolderToItem);
}

export async function fetchChildFolders(
  driveId: string,
  folderId: string,
  token: string
): Promise<FolderItem[]> {
  const raw = await apiFetch<GraphFolderRaw[]>(
    `/api/sharepoint/drives/${driveId}/folders/${folderId}/children`,
    token
  );
  return raw.map(graphFolderToItem);
}

export async function fetchOneDriveRootFolders(token: string): Promise<FolderItem[]> {
  const raw = await apiFetch<GraphFolderRaw[]>("/api/onedrive/folders", token);
  return raw.map(graphFolderToItem);
}

export async function fetchOneDriveChildFolders(
  folderId: string,
  token: string
): Promise<FolderItem[]> {
  const raw = await apiFetch<GraphFolderRaw[]>(
    `/api/onedrive/folders/${folderId}/children`,
    token
  );
  return raw.map(graphFolderToItem);
}

// ── Upload ───────────────────────────────────────────────────────────────────

export async function createUploadJob(
  payload: CreateUploadJobRequest,
  backendToken: string,
  graphAccessToken: string
): Promise<CreateUploadJobResponse> {
  const res = await fetch(`${BACKEND_URL}/api/upload-jobs`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${backendToken}`,
      "X-Graph-Access-Token": graphAccessToken,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Create upload job failed (${res.status}): ${text}`);
  }

  return res.json() as Promise<CreateUploadJobResponse>;
}

export async function fetchUploadJobs(): Promise<UploadJobSummary[]> {
  const data = await backendApiFetch<UploadJobsResponse>("/api/upload-jobs");
  return data.jobs;
}

export async function fetchUploadJob(jobId: string): Promise<UploadJobSummary> {
  return backendApiFetch<UploadJobSummary>(`/api/upload-jobs/${encodeURIComponent(jobId)}`);
}

export async function fetchUploadJobItems(jobId: string): Promise<UploadJobItemsResponse["items"]> {
  const data = await backendApiFetch<UploadJobItemsResponse>(
    `/api/upload-jobs/${encodeURIComponent(jobId)}/items`
  );
  return data.items;
}

export async function streamUploadJobs(
  onSnapshot: (snapshot: UploadJobsSnapshot) => void,
  onError: (message: string) => void
): Promise<() => void> {
  const token = await getBackendToken();
  const controller = new AbortController();

  fetch(`${BACKEND_URL}/api/upload-jobs/stream`, {
    headers: { Authorization: `Bearer ${token}` },
    signal: controller.signal,
  })
    .then(async (res) => {
      if (!res.ok) throw new Error(`Stream failed (${res.status})`);
      if (!res.body) throw new Error("Progress stream is not supported in this browser.");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";

        for (const event of events) {
          const dataLine = event
            .split("\n")
            .find((line) => line.startsWith("data: "));
          if (!dataLine) continue;

          const payload = JSON.parse(dataLine.slice(6)) as UploadJobsSnapshot | { error?: string };
          if ("error" in payload && payload.error) {
            onError(payload.error);
          } else {
            onSnapshot(payload as UploadJobsSnapshot);
          }
        }
      }
    })
    .catch((e: unknown) => {
      if (controller.signal.aborted) return;
      onError(e instanceof Error ? e.message : String(e));
    });

  return () => controller.abort();
}

export async function retryUploadJob(jobId: string): Promise<void> {
  await backendApiFetch<{ retriedCount: number }>(`/api/upload-jobs/${encodeURIComponent(jobId)}/retry`, {
    method: "POST",
  });
}

export async function cancelUploadJob(jobId: string): Promise<void> {
  await backendApiFetch<{ success: boolean }>(`/api/upload-jobs/${encodeURIComponent(jobId)}/cancel`, {
    method: "POST",
  });
}
